import { useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronDown, PlugZap } from "lucide-react";

/**
 * Resolve charger connection status from various API response shapes.
 */
export function resolveChargerStatus(charger) {
  if (charger.isConnected != null) {
    return charger.isConnected ? "online" : "offline";
  }
  return charger.status || "offline";
}

/**
 * Build a site meta description string from site object fields.
 */
export function buildSiteMetaString(site) {
  const siteId = site.siteId || site.id;
  const parts = ["Site ID: " + siteId];
  if (site.chargerCount) parts.push(site.chargerCount + " chargers");
  if (site.country) parts.push(site.country);
  return parts.join(" · ");
}

/* ── Tree Node (expandable) ── */
export function TreeNode({ icon, label, tag, tagColor, meta, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const childCount = Array.isArray(children) ? children.length : children ? 1 : 0;

  return (
    <div className="tree-node">
      <div
        className="tree-node__header"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(o => !o);
          }
        }}
      >
        <span className="tree-node__toggle">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        <span className="tree-node__icon">{icon}</span>
        <span className="tree-node__label">{label}</span>
        <span className={`tree-node__tag tree-node__tag--${tagColor}`}>{tag}</span>
        {childCount > 0 && <span className="tree-node__count">{childCount}</span>}
      </div>
      {meta && <div className="tree-node__meta">{meta}</div>}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="tree-node__children"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Tree Leaf (charger) ── */
const STATUS_COLORS = {
  online: "#10b981",
  charging: "#3b82f6",
  faulted: "#ef4444",
  offline: "#9ca3af",
};

export function TreeLeaf({ charger }) {
  const serial = charger.serialNumber || charger.serial || charger.id;
  const model = charger.modelName || charger.model || charger.sku || "";
  const status = resolveChargerStatus(charger);
  const power = charger.power || charger.maxPower || "";

  return (
    <div className="tree-leaf">
      <span className="tree-leaf__dot" style={{ background: STATUS_COLORS[status] || "#9ca3af" }} />
      <PlugZap size={13} className="tree-leaf__icon" />
      <span className="tree-leaf__serial">{serial}</span>
      {model && <span className="tree-leaf__model">{model}</span>}
      <span className={`tree-leaf__status tree-leaf__status--${status}`}>{status}</span>
      {power && <span className="tree-leaf__power">{power} kW</span>}
    </div>
  );
}

/* ── KPI Badge ── */
export function KpiBadge({ icon: Icon, value, label, color }) {
  return (
    <div className={`kpi-badge kpi-badge--${color}`}>
      <Icon size={15} />
      <span className="kpi-badge__value">{value}</span>
      <span className="kpi-badge__label">{label}</span>
    </div>
  );
}

/* ── PropTypes ── */
TreeNode.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  tag: PropTypes.string,
  tagColor: PropTypes.string,
  meta: PropTypes.string,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node,
};

TreeLeaf.propTypes = {
  charger: PropTypes.object.isRequired,
};

KpiBadge.propTypes = {
  icon: PropTypes.elementType.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};
